import { atom } from "jotai";
import {
  EPHEMERAL_NODE_TYPES,
  PERSISTED_NODES,
} from "@zapier/canvas-constants";
import { ComposerContextType } from "@zapier/agent-system";
import { canvasNameAtom, canvasDescriptionAtom } from "../canvas";
import { nodesAtom } from "../nodes";
import { edgesAtom } from "../edges";
import { sessionAtom } from "../account";
import { isGeneratingTourAtom } from "./isGeneratingTourAtom";

type StartTourParams = {
  canvasName?: string;
  canvasDescription?: string | null;
  nodes?: any[];
  edges?: any[];
  composerHandleSubmit: NonNullable<ComposerContextType["handleSubmit"]>;
};

const NODE_TYPES_TO_EXCLUDE_FROM_TOUR = [
  ...EPHEMERAL_NODE_TYPES.filter((type) => type !== "zapStep"),
  ...PERSISTED_NODES.filter((type) => type === "text" || type === "group"),
];

export const startTourSetter = atom(
  null,
  (get, set, params: StartTourParams) => {
    const {
      canvasName,
      canvasDescription,
      nodes,
      edges,
      composerHandleSubmit,
    } = params;

    // Only allow tour generation for logged-in users
    const session = get(sessionAtom);

    if (!session?.is_logged_in) {
      return;
    }

    // Get values from atoms if not provided
    const canvasNameValue = canvasName ?? get(canvasNameAtom);
    const canvasDescriptionValue =
      canvasDescription ?? get(canvasDescriptionAtom);
    const canvasNodes = (nodes ?? get(nodesAtom)).filter(
      (node) => !NODE_TYPES_TO_EXCLUDE_FROM_TOUR.includes(node.type),
    );
    const canvasEdges = edges ?? get(edgesAtom);

    set(isGeneratingTourAtom, true);

    try {
      composerHandleSubmit(
        {
          target: { value: "start tour" },
        },
        {
          headers: {
            model: "fast",
          },
          data: {
            canvas: {
              name: canvasNameValue,
              description: canvasDescriptionValue,
            },
            nodes: canvasNodes,
            edges: canvasEdges,
          },
        },
      );

      /* renderAnnotationNodesSetter will set isGeneratingTourAtom to false on success.
       * Doing it here will make the loader disappear too early.
       * */
    } catch (error) {
      console.error("Failed to start tour:", error);
      set(isGeneratingTourAtom, false);
    }
  },
);

startTourSetter.debugLabel = "startTourSetter";
